import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// Straight at the service rather than through the appointments barrel, which
// would pull in that feature's screens behind five functions.
import {
  cancelClientAppointment,
  completeClientAppointment,
  createAppointment,
  fetchDayDiary,
  rescheduleClientAppointment,
} from "../../appointments/services/appointmentsService";
import { bySoonest } from "../../appointments/appointments";
import {
  DAY_DIARY_QUERY_PREFIX,
  invalidateAppointmentReads,
} from "../../appointments/appointmentCaches";
import type {
  AppointmentAttendee,
  ClientAppointment,
  NewClientAppointment,
  RescheduleAppointment,
} from "../../appointments/types/appointments.types";
import { isSameDay } from "../myTask";

export type DayTasksStatus = "loading" | "ready" | "failed";

export interface DayTasksState {
  /** Everything booked on the day, soonest first. */
  appointments: ClientAppointment[];
  status: DayTasksStatus;
  /** The server's reason for a failed load, where it gave one. */
  error?: string;
  /** What the failed state's retry calls. */
  reload: () => void;
  /**
   * Books one with whoever the dialog picked — a client or an enquiry — and
   * hands back the row as stored.
   *
   * An attendee rather than a client id, because this screen is the one place
   * either can be booked: it is a day rather than a person, so who the hour
   * is with is a question it has to ask, and "an enquiry" is now one of the
   * answers. See `AppointmentAttendee`.
   */
  schedule: (
    attendee: AppointmentAttendee,
    input: NewClientAppointment
  ) => Promise<ClientAppointment>;
  /** Moves one — possibly clean off this day. */
  reschedule: (
    appointmentId: string,
    input: RescheduleAppointment
  ) => Promise<ClientAppointment>;
  /** Calls one off, leaving it on the day as cancelled. */
  cancel: (appointmentId: string, reason: string) => Promise<ClientAppointment>;
  /** Says the session took place, with the practitioner’s note on it. */
  complete: (
    appointmentId: string,
    note?: string
  ) => Promise<ClientAppointment>;
}

/** Handed out before the first load lands, and the same array every time. */
const EMPTY: ClientAppointment[] = [];

/** `2026-10-01` — the day as a cache key, and never shown to anybody. */
export const dayKeyOf = (day: Date): string => {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
};

/**
 * One entry per day, so moving along the strip is a new question each time.
 *
 * `diary` rather than `day`, which is already taken: the booking dialogs cache
 * the same window under `["appointments", "day", …]` with everything settled
 * filtered out of it. Two readings of one endpoint, and a shared key would
 * have the dialogs quietly handing this screen a day with its cancellations
 * missing.
 */
export const dayTasksQueryKey = (day: Date) =>
  [...DAY_DIARY_QUERY_PREFIX, dayKeyOf(day)] as const;

/**
 * One day's diary, across every client — what My task is built on.
 *
 * Cached per day through TanStack Query, so stepping back to a day already
 * looked at draws immediately while a fresh read runs behind it. Every write
 * answers with the whole row as the server now holds it, and that row is
 * written through `setQueryData` rather than refetched, so the day carries the
 * write without a round trip and a remount does not flash the pre-write row.
 * The same arrangement `useClientAppointments` makes, for the same reasons.
 *
 * One thing is this hook's own: a write can move an appointment off the day
 * being looked at. Rescheduling a two o'clock to tomorrow leaves a row in
 * today's cache that is no longer today's, so `put` below files the answer on
 * the day it now belongs to rather than the day it was pressed on.
 *
 * A short `staleTime`, because a diary moves under a screen that is open — a
 * colleague books into the same afternoon — and this screen is one somebody
 * leaves open all morning.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useDayTasks(day: Date): DayTasksState {
  const getToken = useTokenGetter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = dayTasksQueryKey(day);
  // The key is a string, so the day itself can change identity every render
  // without re-asking anything.
  const key = dayKeyOf(day);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchDayDiary(getToken, day, signal),
    staleTime: 30_000,
  });

  const error =
    query.isError && query.error instanceof ApiError
      ? query.error.message
      : query.isError
      ? "We could not reach the server. Check your connection and try again."
      : undefined;

  // Only fires when there is nothing cached to draw in its place.
  useEffect(() => {
    if (!query.isError) return;
    console.error("day appointments failed to load:", query.error);
    toast.error("Could not load this day’s appointments", error ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error, key]);

  /**
   * Files a row on the day it belongs to.
   *
   * Two things happen, and which of them depends on where the row landed.
   * Where it belongs to the day in hand it replaces its old self or joins the
   * list, and the day is re-sorted — a reschedule may have moved it past its
   * neighbours. Where it has moved to another day it is dropped from this one.
   *
   * Either way every other cache holding the row is then marked stale, which
   * covers the day it moved to as well as the client's own history and the
   * Meeting dialog — see `invalidateAppointmentReads`.
   */
  const put = useCallback(
    (row: ClientAppointment) => {
      const landed = new Date(row.startsAt);
      const here = !Number.isNaN(landed.getTime()) && isSameDay(landed, day);

      queryClient.setQueryData<ClientAppointment[]>(
        queryKey,
        (current: ClientAppointment[] | undefined) => {
          const list = current ?? EMPTY;
          if (!here) {
            return list.filter(
              (appointment: ClientAppointment) => appointment.id !== row.id
            );
          }

          const known = list.some(
            (appointment: ClientAppointment) => appointment.id === row.id
          );
          const next = known
            ? list.map((appointment: ClientAppointment) =>
                appointment.id === row.id ? row : appointment
              )
            : [...list, row];
          return next.sort(bySoonest);
        }
      );

      invalidateAppointmentReads(queryClient, row);
    },
    // `day` is read through `isSameDay` only, and `key` is what actually moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, key]
  );

  const schedule = useCallback(
    async (attendee: AppointmentAttendee, input: NewClientAppointment) => {
      const booked = await createAppointment(getToken, attendee, input);
      put(booked);
      return booked;
    },
    [getToken, put]
  );

  const reschedule = useCallback(
    async (appointmentId: string, input: RescheduleAppointment) => {
      const moved = await rescheduleClientAppointment(
        getToken,
        appointmentId,
        input
      );
      put(moved);
      return moved;
    },
    [getToken, put]
  );

  const cancel = useCallback(
    async (appointmentId: string, reason: string) => {
      const called = await cancelClientAppointment(
        getToken,
        appointmentId,
        reason
      );
      put(called);
      return called;
    },
    [getToken, put]
  );

  const complete = useCallback(
    async (appointmentId: string, note = "") => {
      const done = await completeClientAppointment(
        getToken,
        appointmentId,
        note
      );
      put(done);
      return done;
    },
    [getToken, put]
  );

  return {
    appointments: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error,
    reload: () => {
      void query.refetch();
    },
    schedule,
    reschedule,
    cancel,
    complete,
  };
}

export default useDayTasks;
